import { cn } from 'lib/helpers/utility';

import {
  usePaymentContext,
  usePaymentContextApi
} from 'components/Payments/NewPayment/PaymentContent/context/PaymentContext';
import DateInputPicker from 'components/shared/DateInputPicker/DateInputPicker';
import Icon from 'components/shared/Icon/Icon';
import Select from 'components/shared/Select/Select';
import Typography from 'components/shared/Typography/Typography';

import {
  CHARGE_TIME_OPTIONS,
  ENDS_OPTIONS,
  REPEAT_EVENT_OPTIONS,
  generateDayEventOptions,
  generateOnEventOptions,
  isOccurrenceOnEvent
} from '../../../../lib/helpers';
import InputWithUnit from '../InputWithUnit';

import LinkToAppointment from './LinkToAppointment';

const AutoRecurring = ({ patientId: reqPatientId, isEdit = false }) => {
  const { values } = usePaymentContext();
  const { setFieldValue } = usePaymentContextApi();

  const recurringPayment = values?.recurringPayment;
  const patientId = reqPatientId || values?.patientId;

  const handleChange = (key, val) => {
    if (!recurringPayment || !key) return;

    const next = { ...recurringPayment, [key]: val };

    if (key === 'startDate' && val) {
      const [mm, dd] = String(val).split('/');
      const day = Number(dd);
      const month = Number(mm);
      if (!isOccurrenceOnEvent(recurringPayment.onEvent)) {
        if (recurringPayment.repeatEvent === 'yearly') {
          if (month) next.onEvent = month;
          if (day) next.dayEvent = day;
        } else if (recurringPayment.repeatEvent === 'monthly' && day) {
          next.dayEvent = day > 28 ? 'last' : day;
        }
      }
    }

    if (key === 'onEvent') {
      const wasOccurrence = isOccurrenceOnEvent(recurringPayment.onEvent);
      const isOccurrence = isOccurrenceOnEvent(val);
      if (wasOccurrence !== isOccurrence) {
        next.dayEvent = isOccurrence ? 0 : 1;
      } else {
        const nextDayEventOptions = generateDayEventOptions(val);
        const stillValid = nextDayEventOptions.some(
          (option) => option.value === recurringPayment.dayEvent
        );
        if (!stillValid) {
          next.dayEvent = nextDayEventOptions[0]?.value ?? 1;
        }
      }
    }

    setFieldValue('recurringPayment', next);
  };

  const isDisabled = Boolean(recurringPayment?.linkToRecurringAppointment);
  const chargeDisabled = isDisabled || isEdit;

  const currentChargeTime = CHARGE_TIME_OPTIONS.find(
    (option) => option.value === recurringPayment?.chargeTime
  );

  const currentRepeatEvent = REPEAT_EVENT_OPTIONS.find(
    (option) => option.value === recurringPayment?.repeatEvent
  );

  const currentEnds = ENDS_OPTIONS.find((option) => option.value === recurringPayment?.ends);

  const eventOptions = generateOnEventOptions(recurringPayment?.repeatEvent);

  const currentOnEvent = eventOptions.find((option) => option.value === recurringPayment?.onEvent);

  const dayEventOptions = generateDayEventOptions(recurringPayment?.onEvent);

  const currentDayEvent = dayEventOptions.find(
    (option) => option.value === recurringPayment?.dayEvent
  );

  return (
    <div className="flex flex-col gap-4">
      <Typography variant="p" size="small">
        Charge a fixed amount on a repeating schedule (weekly, monthly, yearly).
      </Typography>
      <div className="flex flex-col gap-3">
        {!isEdit && <LinkToAppointment patientId={patientId} />}
        <Select
          inputId="charge-time"
          label="When to charge"
          value={currentChargeTime}
          options={CHARGE_TIME_OPTIONS}
          onChange={(option) => {
            const next = { ...recurringPayment, chargeTime: option?.value, startDate: null };
            setFieldValue('recurringPayment', next);
          }}
          name="chargeTime"
          parentClassName="flex-1"
          isClearable={false}
          disabled={chargeDisabled}
          menuPortalTarget={document.body}
        />
        {currentChargeTime?.value === 'future-date' && (
          <div className="flex flex-col gap-1">
            <DateInputPicker
              inputId="charge-date"
              label="Start date"
              isDateOnly
              value={recurringPayment?.startDate}
              onChange={(val) => handleChange('startDate', val)}
              name="startDate"
              parentClassName="flex-1"
              isClearable={false}
              disabled={chargeDisabled}
              minDate={new Date()}
            />
            <div className="flex items-center gap-0.5">
              <Icon icon="info-filled" size={14} className={cn(isEdit && 'opacity-50')} />
              <Typography variant="p" size="xs" disabled={isEdit} color="neutral-600">
                The plan becomes active on this date.
              </Typography>
            </div>
          </div>
        )}
        <div className="flex items-center gap-3">
          <Select
            inputId="repeat-event"
            label="Repeat"
            value={currentRepeatEvent}
            options={REPEAT_EVENT_OPTIONS}
            onChange={(option) => {
              const next = {
                ...recurringPayment,
                repeatEvent: option?.value,
                repeatEvery: 1
              };
              const today = new Date();
              if (option?.value === 'yearly') {
                next.onEvent = today.getMonth() + 1;
                next.dayEvent = today.getDate();
              } else if (option?.value === 'weekly') {
                next.onEvent = today.getDay();
                next.dayEvent = 1;
              } else {
                next.onEvent = 'day';
                const d = today.getDate();
                next.dayEvent = d > 28 ? 'last' : d;
              }
              setFieldValue('recurringPayment', next);
            }}
            name="repeatEvent"
            icon="repeat-mono"
            parentClassName="w-1/2"
            isClearable={false}
            menuPortalTarget={document.body}
            disabled={isDisabled}
          />
          {currentRepeatEvent?.value !== 'yearly' && (
            <InputWithUnit
              data-qa="repeat-every"
              label="Repeat every"
              onChange={(e) => {
                const positive = Math.floor(Math.abs(e.target?.value)) || 1;
                handleChange('repeatEvery', positive);
              }}
              value={recurringPayment?.repeatEvery}
              name="repeatEvery"
              min={1}
              disabled={isDisabled}
              unitLabel={`${currentRepeatEvent?.unitLabel}(s)`}
            />
          )}
        </div>
        {currentChargeTime?.value === 'future-date' && (
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-3">
              <Select
                inputId="on-event"
                label="On"
                value={currentOnEvent}
                options={eventOptions}
                onChange={(option) => handleChange('onEvent', option?.value)}
                name="onEvent"
                parentClassName="flex-1"
                isClearable={false}
                menuPortalTarget={document.body}
                disabled={isDisabled}
              />
              {currentRepeatEvent?.value !== 'weekly' && (
                <Select
                  inputId="day-event"
                  label="day"
                  value={currentDayEvent}
                  options={dayEventOptions}
                  onChange={(option) => handleChange('dayEvent', option?.value)}
                  name="dayEvent"
                  parentClassName="flex-1"
                  isClearable={false}
                  menuPortalTarget={document.body}
                  disabled={isDisabled}
                />
              )}
            </div>
            {recurringPayment?.dayEvent === 'last' && currentRepeatEvent?.value === 'monthly' && (
              <div className="flex items-center gap-0.5">
                <Icon icon="info-filled" size={14} />
                <Typography variant="p" size="xs" color="neutral-600">
                  Charges on the last day of each month.
                </Typography>
              </div>
            )}
          </div>
        )}
        <div className="flex items-center gap-3">
          <Select
            inputId="ends"
            label="Ends"
            value={currentEnds}
            options={ENDS_OPTIONS}
            onChange={(option) => handleChange('ends', option?.value)}
            name="ends"
            parentClassName="w-1/2"
            isClearable={false}
            menuPortalTarget={document.body}
            disabled={isDisabled}
          />
          {currentEnds?.value === 'on-date' && (
            <DateInputPicker
              inputId="ends-date"
              label="End date"
              isDateOnly
              value={recurringPayment?.endsDate}
              onChange={(val) => handleChange('endsDate', val)}
              name="endsDate"
              parentClassName="flex-1"
              isClearable={false}
              disabled={isDisabled}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AutoRecurring;
